/**
 * Flow C session + computation — GET /api/appointments/{id}/session. Used both by
 * `ConsultCompleteDialog` (a single, non-polling read for context before an action) and
 * `ConsultRoomFrame` (`{poll: true}`, so the patient's page reflects the session moving
 * through scheduled -> in_progress -> computed without a manual refresh).
 *
 * Polling stops itself once the session reaches a terminal status (contract §7:
 * captured | void_no_show | cancelled) — there is nothing left to learn by continuing
 * to ask, and continuing would just be a background timer with no purpose. `expired`
 * was an eighth SESSION_STATES member nothing ever set (a lapsed hold is `cancelled`)
 * and the backend dropped it; the PAYMENT state `expired` is a different vocabulary
 * and stays.
 *
 * `gatewayApi` is an optional trailing param defaulting to the module singleton,
 * mirroring the component-level DI rule (contract §9).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import defaultGatewayApi from "../api";
import { SESSION_MONEY_FINAL_STATES } from "../contract";

const POLL_INTERVAL_MS = 4000;

export function useConsultSession(appointmentId, { poll = false } = {}, gatewayApi = defaultGatewayApi) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const timerRef = useRef(null);

  const reload = useCallback(async () => {
    if (!appointmentId) {
      setLoading(false);
      return null;
    }
    setLoading(true);
    try {
      const session = await gatewayApi.getConsultSession(appointmentId);
      setData(session);
      setError(null);
      return session;
    } catch (e) {
      setError(e);
      return null;
    } finally {
      setLoading(false);
    }
  }, [appointmentId, gatewayApi]);

  useEffect(() => {
    let cancelled = false;

    const tick = async () => {
      const session = await reload();
      if (cancelled || !poll) return;
      if (session && SESSION_MONEY_FINAL_STATES.includes(session.status)) return;
      timerRef.current = setTimeout(tick, POLL_INTERVAL_MS);
    };

    tick();

    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // `reload` is stable per (appointmentId, gatewayApi) via its own useCallback deps;
    // re-running this effect on every render of `reload` would restart the poll loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointmentId, poll]);

  return { data, loading, error, reload };
}

export default useConsultSession;
